import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, ChevronUp, Dumbbell, GripVertical, Pencil, Trash2, Video, X } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../../../contexts/AuthContext';
import RecoveryMinSecInputs from './RecoveryMinSecInputs';
import WorkoutRepsInputs from './WorkoutRepsInputs';
import PyramidSeriesEditor from './PyramidSeriesEditor';
import { GYM_WORKOUT_SET_TYPE_OPTIONS, getWorkoutSetTypeLabel, getWorkoutSetTypeBadgeClass } from '../../../constants/workoutSetTypes';
import { formatWorkoutRepsTarget } from '../../../constants/workoutRepsMode';
import { isPyramidExercise, pyramidDisplayReps } from '../../../constants/workoutSeriesPlan';

const inputClass =
    'w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500';

function exerciseSummaryLine(exercise) {
    const reps = formatWorkoutRepsTarget(exercise) || exercise.ripetizioni || 0;
    const parts = [`${exercise.serie || 0} × ${reps}`];
    if (exercise.is_cardio !== 1 && Number(exercise.peso) > 0) {
        parts.push(`@ ${exercise.peso} kg`);
    }
    return parts.join(' ');
}

function formatMuscleLabel(muscle) {
    if (!muscle) return null;
    if (typeof muscle === 'string') return muscle;
    if (typeof muscle === 'object' && muscle.name) return muscle.name;
    return null;
}

function getExerciseMuscleLabel(exercise) {
    return formatMuscleLabel(exercise.primary_muscle) || exercise.gruppo_muscolare || null;
}

function catalogExerciseId(exercise) {
    const id = exercise.esercizio_id || exercise.exercise_id || null;
    const n = Number(id);
    return Number.isFinite(n) && n > 0 ? n : null;
}

export default function WorkoutSortableExerciseItem({
    exercise,
    previousExercise,
    onUpdate,
    onRemove,
    onToggleDetails,
    onGymNameChange,
    isExpanded,
    isFirst,
}) {
    const { t } = useTranslation();
    const { getAuthenticatedApi } = useAuth();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: exercise.id });
    const [editingName, setEditingName] = useState(false);
    const [nameDraft, setNameDraft] = useState('');
    const [savingName, setSavingName] = useState(false);
    const [nameError, setNameError] = useState('');
    const [youtubeDraft, setYoutubeDraft] = useState(exercise.youtube_url || '');
    const [committedYoutubeUrl, setCommittedYoutubeUrl] = useState(
        () => (exercise.youtube_url || '').trim()
    );
    const [savingYoutube, setSavingYoutube] = useState(false);
    const [youtubeError, setYoutubeError] = useState('');
    const [youtubeSavedFlash, setYoutubeSavedFlash] = useState(false);

    useEffect(() => {
        const fromExercise = (exercise.youtube_url || '').trim();
        setYoutubeDraft(fromExercise);
        setCommittedYoutubeUrl(fromExercise);
    }, [exercise.youtube_url, exercise.id]);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    };

    const muscleLabel = getExerciseMuscleLabel(exercise);
    const catalogId = catalogExerciseId(exercise);
    const catalogName = exercise.nome_catalogo || null;
    const isAliased = Boolean(exercise.nome_palestra) || Boolean(catalogName && catalogName !== exercise.nome);
    const youtubeDirty = youtubeDraft.trim() !== committedYoutubeUrl;

    const startRename = () => {
        setNameDraft(exercise.nome || '');
        setNameError('');
        setEditingName(true);
    };

    const applyAliasPatch = (patch) => {
        if (onGymNameChange && catalogId) {
            onGymNameChange(catalogId, patch);
            return;
        }
        Object.entries(patch).forEach(([key, value]) => {
            onUpdate(exercise.id, key, value);
        });
    };

    const applyNamePatch = (nextName, customName) => {
        applyAliasPatch({
            nome: nextName,
            nome_palestra: customName || null,
            nome_catalogo: catalogName || exercise.nome,
        });
    };

    const isValidYoutubeUrl = (raw) => {
        const v = (raw || '').trim();
        if (!v) return true;
        return /^(https?:\/\/)?((www|m)\.)?(youtube\.com|youtu\.be|youtube-nocookie\.com)\//i.test(v);
    };

    const saveYoutubeUrl = async (raw, { clear = false } = {}) => {
        if (!catalogId || savingYoutube) return;
        const trimmed = clear ? '' : (raw || '').trim();
        if (trimmed && !isValidYoutubeUrl(trimmed)) {
            setYoutubeError(t('gym.membersManager.workoutModal.youtubeLinkInvalid'));
            return;
        }
        setSavingYoutube(true);
        setYoutubeError('');
        try {
            const api = getAuthenticatedApi();
            const res = await api.post('/gym_exercise_aliases.php', {
                esercizio_id: catalogId,
                youtube_url: trimmed,
            });
            const saved = res.data?.youtube_url ?? (trimmed === '' ? null : trimmed);
            const committed = (saved || '').trim();
            setYoutubeDraft(committed);
            setCommittedYoutubeUrl(committed);
            applyAliasPatch({ youtube_url: committed || null });
            setYoutubeSavedFlash(true);
            setTimeout(() => setYoutubeSavedFlash(false), 1500);
        } catch (err) {
            setYoutubeError(t('gym.membersManager.workoutModal.youtubeLinkSaveError'));
        } finally {
            setSavingYoutube(false);
        }
    };

    const saveGymName = async (raw) => {
        if (!catalogId || savingName) return;
        const trimmed = (raw || '').trim();
        const originalCatalog = (catalogName || exercise.nome || '').trim();
        const customName = trimmed === '' || trimmed === originalCatalog ? '' : trimmed;
        setSavingName(true);
        setNameError('');
        try {
            const api = getAuthenticatedApi();
            const res = await api.post('/gym_exercise_aliases.php', {
                esercizio_id: catalogId,
                custom_name: customName,
            });
            const saved = res.data?.custom_name ?? (customName === '' ? null : customName);
            const nextName = saved || originalCatalog || trimmed;
            applyNamePatch(nextName, saved);
            setEditingName(false);
        } catch (err) {
            setNameError(t('gym.membersManager.workoutModal.renameSaveError'));
        } finally {
            setSavingName(false);
        }
    };

    const restoreCatalogName = async () => {
        if (!catalogId) return;
        setSavingName(true);
        setNameError('');
        try {
            const api = getAuthenticatedApi();
            await api.post('/gym_exercise_aliases.php', {
                esercizio_id: catalogId,
                custom_name: '',
            });
            applyNamePatch(catalogName || exercise.nome, null);
            setEditingName(false);
        } catch (err) {
            setNameError(t('gym.membersManager.workoutModal.renameSaveError'));
        } finally {
            setSavingName(false);
        }
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`bg-white dark:bg-gray-900 ${isDragging ? 'z-10 shadow-md' : ''}`}
        >
            <div className="flex items-center gap-1.5 px-2 py-2 sm:gap-2 sm:px-3">
                <button
                    type="button"
                    {...attributes}
                    {...listeners}
                    className="shrink-0 cursor-grab rounded p-1 text-gray-400 hover:text-indigo-600 active:cursor-grabbing dark:hover:text-indigo-300"
                    aria-label={t('gym.membersManager.workoutModal.dragExercise')}
                >
                    <GripVertical className="h-4 w-4" aria-hidden />
                </button>

                <div className="h-8 w-8 shrink-0 overflow-hidden rounded-md bg-gray-100 dark:bg-gray-800">
                    {exercise.immagine_nome ? (
                        <img
                            src={`/api/serve_image.php?filename=${encodeURIComponent(exercise.immagine_nome)}`}
                            alt=""
                            className="h-full w-full object-cover select-none"
                            onContextMenu={(e) => e.preventDefault()}
                            draggable={false}
                            onError={(e) => {
                                e.currentTarget.style.display = 'none';
                            }}
                        />
                    ) : (
                        <span className="flex h-full w-full items-center justify-center">
                            <Dumbbell className="h-3.5 w-3.5 text-gray-400 dark:text-white/40" aria-hidden />
                        </span>
                    )}
                </div>

                <div className="min-w-0 flex-1">
                    {editingName ? (
                        <div className="flex items-center gap-1">
                            <input
                                type="text"
                                value={nameDraft}
                                onChange={(e) => setNameDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        saveGymName(nameDraft);
                                    }
                                    if (e.key === 'Escape') {
                                        setEditingName(false);
                                        setNameError('');
                                    }
                                }}
                                maxLength={255}
                                disabled={savingName}
                                autoFocus
                                className={`${inputClass} py-1 text-sm font-semibold`}
                                aria-label={t('gym.membersManager.workoutModal.renameExercise')}
                            />
                            <button
                                type="button"
                                onClick={() => saveGymName(nameDraft)}
                                disabled={savingName}
                                className="rounded p-1 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50 dark:text-emerald-400 dark:hover:bg-emerald-950/40"
                                title={t('gym.membersManager.workoutModal.saveChanges')}
                            >
                                <Check className="h-3.5 w-3.5" />
                            </button>
                            <button
                                type="button"
                                onClick={() => { setEditingName(false); setNameError(''); }}
                                disabled={savingName}
                                className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10"
                                title={t('gym.membersManager.workoutModal.cancel')}
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    ) : (
                        <div className="flex min-w-0 items-center gap-1">
                            <p className="truncate text-sm font-semibold text-gray-900 dark:text-white" title={exercise.nome}>
                                {exercise.nome}
                            </p>
                            {catalogId ? (
                                <button
                                    type="button"
                                    onClick={startRename}
                                    className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-indigo-600 dark:hover:bg-white/10 dark:hover:text-indigo-300"
                                    title={t('gym.membersManager.workoutModal.renameExercise')}
                                    aria-label={t('gym.membersManager.workoutModal.renameExercise')}
                                >
                                    <Pencil className="h-3 w-3" />
                                </button>
                            ) : null}
                        </div>
                    )}
                    {nameError ? (
                        <p className="mt-0.5 text-[11px] text-red-600 dark:text-red-400">{nameError}</p>
                    ) : null}
                    {isAliased && catalogName ? (
                        <p className="mt-0.5 flex min-w-0 items-center gap-1 truncate text-[11px] text-gray-400 dark:text-white/40">
                            <span className="truncate">{t('gym.membersManager.workoutModal.catalogName', { name: catalogName })}</span>
                            <button
                                type="button"
                                onClick={restoreCatalogName}
                                disabled={savingName}
                                className="shrink-0 text-[11px] font-medium text-indigo-600 hover:underline disabled:opacity-50 dark:text-indigo-300"
                            >
                                {t('gym.membersManager.workoutModal.restoreCatalogName')}
                            </button>
                        </p>
                    ) : null}
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500 dark:text-white/55">
                        <span className="font-medium text-gray-700 dark:text-white/75">{exerciseSummaryLine(exercise)}</span>
                        {muscleLabel ? (
                            <span className="truncate">{muscleLabel}</span>
                        ) : null}
                    </p>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                        {exercise.set_type && exercise.set_type !== 'normal' ? (
                            <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${getWorkoutSetTypeBadgeClass(exercise.set_type)}`}>
                                {getWorkoutSetTypeLabel(exercise.set_type)}
                            </span>
                        ) : null}
                        {exercise.linked_to_previous ? (
                            <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-800 dark:bg-orange-900/40 dark:text-orange-200">
                                {t('gym.membersManager.workouts.detail.linkedBadge')}
                            </span>
                        ) : null}
                        {isPyramidExercise(exercise) && pyramidDisplayReps(exercise) ? (
                            <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                                {pyramidDisplayReps(exercise)}
                            </span>
                        ) : null}
                        {exercise.is_cardio === 1 ? (
                            <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-800 dark:bg-red-900/40 dark:text-red-200">
                                Cardio
                            </span>
                        ) : null}
                    </div>
                </div>

                <div className="flex shrink-0 items-center gap-0.5">
                    <button
                        type="button"
                        onClick={() => onToggleDetails(exercise.id)}
                        className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-white/70 dark:hover:bg-white/10"
                        title={isExpanded ? t('gym.membersManager.workoutModal.hideParameters') : t('gym.membersManager.workoutModal.configureParameters')}
                        aria-expanded={isExpanded}
                    >
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    <button
                        type="button"
                        onClick={() => onRemove(exercise.id)}
                        className="rounded-lg p-1.5 text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                        title={t('gym.membersManager.workoutModal.removeExercise')}
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {isExpanded ? (
                <div className="border-t border-gray-100 bg-gray-50/80 px-3 py-3 dark:border-white/5 dark:bg-gray-800/40 sm:px-4">
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                        {!isPyramidExercise(exercise) ? (
                            <div>
                                <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-white/75">Serie</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={exercise.serie ?? ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        onUpdate(exercise.id, 'serie', value === '' ? '' : parseInt(value, 10) || '');
                                    }}
                                    onBlur={(e) => {
                                        if (e.target.value === '' || e.target.value === '0') {
                                            onUpdate(exercise.id, 'serie', 3);
                                        }
                                    }}
                                    onFocus={(e) => e.target.select()}
                                    className={inputClass}
                                />
                            </div>
                        ) : null}

                        {!isPyramidExercise(exercise) ? <WorkoutRepsInputs exercise={exercise} onUpdate={onUpdate} /> : null}

                        {exercise.is_cardio !== 1 && !isPyramidExercise(exercise) ? (
                            <div>
                                <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-white/75">Peso (kg)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    value={exercise.peso ?? ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        onUpdate(exercise.id, 'peso', value === '' ? '' : parseFloat(value) || '');
                                    }}
                                    onBlur={(e) => {
                                        if (e.target.value === '') {
                                            onUpdate(exercise.id, 'peso', 0);
                                        }
                                    }}
                                    onFocus={(e) => e.target.select()}
                                    className={inputClass}
                                />
                            </div>
                        ) : null}

                        <RecoveryMinSecInputs
                            valueSeconds={exercise.tempo_recupero}
                            onSecondsChange={(sec) => onUpdate(exercise.id, 'tempo_recupero', sec)}
                            defaultTotalSeconds={90}
                            focusColor="emerald"
                            gymDarkInputs
                        />
                    </div>

                    <div className="mt-3">
                        <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-white/75">Tipo di Serie</label>
                        <select
                            value={exercise.set_type || 'normal'}
                            onChange={(e) => onUpdate(exercise.id, 'set_type', e.target.value)}
                            className={inputClass}
                        >
                            {GYM_WORKOUT_SET_TYPE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    <PyramidSeriesEditor exercise={exercise} onUpdate={onUpdate} />

                    {!isFirst && exercise.set_type && exercise.set_type !== 'normal' && previousExercise && exercise.set_type === previousExercise.set_type ? (
                        <div className="mt-3">
                            <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-white/85">
                                <input
                                    type="checkbox"
                                    checked={exercise.linked_to_previous || false}
                                    onChange={(e) => onUpdate(exercise.id, 'linked_to_previous', e.target.checked)}
                                    className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                Collegato all&apos;esercizio precedente
                            </label>
                        </div>
                    ) : null}

                    {exercise.is_cardio !== 1 ? (
                        <div className="mt-3">
                            <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-white/85">
                                <input
                                    type="checkbox"
                                    checked={exercise.is_isometric_scheda === 1}
                                    onChange={(e) => onUpdate(exercise.id, 'is_isometric_scheda', e.target.checked ? 1 : 0)}
                                    className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                Modalità isometrica (secondi)
                            </label>
                        </div>
                    ) : null}

                    {exercise.is_cardio === 1 ? (
                        <div className="mt-3 grid grid-cols-2 gap-3">
                            <div>
                                <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-white/75">Calorie (opz.)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={exercise.calorie || ''}
                                    onChange={(e) => onUpdate(exercise.id, 'calorie', e.target.value ? parseInt(e.target.value, 10) : null)}
                                    className={inputClass}
                                    placeholder="Calorie"
                                />
                            </div>
                            <div>
                                <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-white/75">BPM (opz.)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={exercise.bpm || ''}
                                    onChange={(e) => onUpdate(exercise.id, 'bpm', e.target.value ? parseInt(e.target.value, 10) : null)}
                                    className={inputClass}
                                    placeholder="BPM"
                                />
                            </div>
                        </div>
                    ) : null}

                    <div className="mt-3">
                        <label className="mb-0.5 flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-white/75">
                            <Video className="h-3.5 w-3.5 text-red-600 dark:text-red-400" aria-hidden />
                            {t('gym.membersManager.workoutModal.youtubeLink')}
                        </label>
                        <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center">
                            <input
                                type="url"
                                value={youtubeDraft}
                                onChange={(e) => {
                                    setYoutubeDraft(e.target.value);
                                    setYoutubeError('');
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        saveYoutubeUrl(youtubeDraft);
                                    }
                                }}
                                disabled={!catalogId || savingYoutube}
                                maxLength={512}
                                className={inputClass}
                                placeholder={t('gym.membersManager.workoutModal.youtubeLinkPlaceholder')}
                            />
                            <div className="flex shrink-0 items-center gap-1">
                                {youtubeDirty ? (
                                    <button
                                        type="button"
                                        onClick={() => saveYoutubeUrl(youtubeDraft)}
                                        disabled={!catalogId || savingYoutube}
                                        className="rounded-md bg-indigo-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                                    >
                                        {t('gym.membersManager.workoutModal.youtubeLinkSave')}
                                    </button>
                                ) : null}
                                {committedYoutubeUrl ? (
                                    <button
                                        type="button"
                                        onClick={() => saveYoutubeUrl('', { clear: true })}
                                        disabled={!catalogId || savingYoutube}
                                        className="rounded-md px-2 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-950/40"
                                    >
                                        {t('gym.membersManager.workoutModal.youtubeLinkClear')}
                                    </button>
                                ) : null}
                            </div>
                        </div>
                        <p className="mt-0.5 text-[11px] text-gray-400 dark:text-white/40">
                            {t('gym.membersManager.workoutModal.youtubeLinkHint')}
                            {youtubeSavedFlash ? (
                                <span className="ml-1 font-medium text-emerald-600 dark:text-emerald-400">✓</span>
                            ) : null}
                        </p>
                        {youtubeError ? (
                            <p className="mt-0.5 text-[11px] text-red-600 dark:text-red-400">{youtubeError}</p>
                        ) : null}
                    </div>

                    <div className="mt-3">
                        <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-white/75">Note trainer</label>
                        <textarea
                            value={exercise.notes?.trainer ?? ''}
                            onChange={(e) => {
                                const existingNotes = exercise.notes || { trainer: null, user: null, system: null };
                                onUpdate(exercise.id, 'notes', {
                                    trainer: e.target.value || null,
                                    user: existingNotes.user || null,
                                    system: existingNotes.system || null,
                                });
                            }}
                            rows={2}
                            maxLength={500}
                            className={inputClass}
                            placeholder="Istruzioni per il cliente durante l'allenamento"
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
}
